import { CheckCircle2 } from 'lucide-react'
import type { AnswerInputProps } from '../QuestionRenderer'

export function TotalInput({ question, value, onChange, onSubmit }: AnswerInputProps<number>) {
  const total = value ?? Number(question.settings.totalPreviewValue ?? 0)
  const sources = question.settings.totalSourceQuestionIds ?? []
  const formulaDescription = describeFormula(
    question.settings.totalMode,
    question.settings.totalFormula,
    sources.length,
  )

  return (
    <div
      className="rounded-lg border p-5"
      style={{
        background: 'color-mix(in srgb, var(--form-bg,#F8FAFC) 82%, white)',
        borderColor: 'color-mix(in srgb, var(--form-accent,#6366F1) 24%, transparent)',
        borderRadius: 'var(--form-radius,8px)',
      }}
    >
      <div
        className="text-sm font-medium"
        style={{
          color: 'var(--form-answer,#64748B)',
          fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
        }}
      >
        {question.settings.totalPrefix ?? '計算結果'}
      </div>
      <div
        className="mt-2 flex flex-wrap items-end gap-2 font-semibold"
        style={{
          color: 'var(--form-text,#0F172A)',
          fontFamily: 'var(--form-heading-font,var(--form-font,Geist))',
          fontSize: 'clamp(2rem, 6vw, 4rem)',
          lineHeight: 1,
        }}
      >
        <span>{new Intl.NumberFormat('zh-TW').format(total)}</span>
        {question.settings.totalUnit && (
          <span className="pb-1 text-xl">{question.settings.totalUnit}</span>
        )}
        {question.settings.totalSuffix && (
          <span className="pb-1 text-xl">{question.settings.totalSuffix}</span>
        )}
      </div>
      <div className="mt-3 text-sm" style={{ color: 'var(--form-answer,#64748B)' }}>
        {formulaDescription}
      </div>
      <div
        className="mt-4 flex flex-wrap gap-2 text-xs"
        style={{ color: 'var(--form-answer,#64748B)' }}
      >
        <span
          className="rounded-full border px-2.5 py-1"
          style={{
            borderColor: 'color-mix(in srgb, var(--form-accent,#6366F1) 24%, transparent)',
          }}
        >
          {sources.length > 0 ? `已連結 ${sources.length} 個來源題目` : '尚未選擇來源題目'}
        </span>
        <span
          className="rounded-full border px-2.5 py-1"
          style={{
            borderColor: 'color-mix(in srgb, var(--form-accent,#6366F1) 24%, transparent)',
          }}
        >
          mock-safe 公式預覽
        </span>
      </div>
      {question.settings.totalMode === 'custom' && question.settings.totalFormula && (
        <div
          className="mt-3 rounded-md border px-3 py-2 text-xs"
          style={{ color: 'var(--form-answer,#64748B)' }}
        >
          自訂公式已設定。正式公式內容只在 Builder 設定中顯示，填答頁僅顯示計算摘要。
        </div>
      )}
      <button
        type="button"
        onClick={() => {
          onChange(total)
          window.setTimeout(onSubmit, 120)
        }}
        className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-md px-4 font-medium"
        style={{
          background: 'var(--form-button,#6366F1)',
          borderRadius: 'var(--form-radius,8px)',
          color: 'var(--form-button-text,#FFFFFF)',
          fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
        }}
      >
        <CheckCircle2 className="size-4" />
        {question.settings.buttonText ?? '確認總計'}
      </button>
    </div>
  )
}

function describeFormula(mode: unknown, formula: unknown, sourceCount: number) {
  if (mode === 'average') return `計算方式：平均 ${sourceCount} 個來源題目`
  if (mode === 'count') return `計算方式：計算 ${sourceCount} 個來源題目的填答數`
  if (mode === 'custom') return formula ? '計算方式：自訂公式' : '自訂公式尚未設定'
  return `計算方式：加總 ${sourceCount} 個來源題目`
}
